import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { apiInstance, Dispatch, GENERATE_UPLOAD_LINK_PAYMENT, GET_ELECTRONIC_DOCS, ISupports, REGISTER_PAYMENT_SUPPORT, RootState, VALIDATE_PAYMENT_SUPPORT } from 'shared';
import { downloadDocument, downloadsDescription } from 'shared/utils';
import Swal from 'sweetalert2'

export const useUploadDocuments = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<Dispatch>();
    const { response, supports } = useSelector((state: RootState) => state.linkSupports);
    const {originalName, id} = useSelector((state: RootState) => state.linkDocument);
    const [hasErrors, setHasErrors] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [document, setDocument] = useState<any>(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nameSupports, setNameSupports] = useState([]);
  
    const typeAccept = ['XLSX'];
    const fileLength = 5;
    const typeAcceptedDocument= ['PDF'];
    const fileLengthDocument = 10;

    useEffect(() => {
      return () => {
        removeFileXlsx();
        dispatch?.linkDocument?.cleanState();
      }
    }, [])
  
    const handleChangeFileXlsx = useCallback((file: any) => {
      setLoading(true);
      setDocument(file);
      if (file && file.size < (5000000)) {
        dispatch.linkDocument.getLinkAsync({
          file: file,
          url: `${GENERATE_UPLOAD_LINK_PAYMENT}`
        })?.then(()=>{
          setDocument(file);
          setLoading(false);
        })
      }
    }, []);  
  
    const removeFileXlsx = useCallback(() => {
      dispatch?.linkDocument?.cleanState();
      setDocument(null);
    }, []);

    const cleanDocuments = () => {
      setDocument(null);
      dispatch?.linkDocument?.cleanState();
      dispatch.linkSupports.cleanSupports();
      setIsDisabled(false);
    }
  
    const uploadXlsx = useCallback(async() => {
      setLoading(true);
      await apiInstance.post(VALIDATE_PAYMENT_SUPPORT, {attachmentId: id}).then((response) => {
        if(response.status === 206){
          downloadDocument(response, 'Se presentan errores en el archivo', downloadsDescription.notificationRips, 'error');
          dispatch?.linkDocument?.cleanState();
          setIsDisabled(false);
        }else{
          Swal.fire({
            title: 'Carga exitosa',
            text: 'El archivo se ha cargado correctamente.',
            icon: 'success',
            timer: 43500,
          }).then(() => {
              dispatch.linkSupports.addSupports(response.data.paymentDocumentName.map((doc: any, i: number) => ({
                id: i+1,
                name: doc,
                code: doc,
              })))
              setIsDisabled(true);
              setNameSupports(response.data);
            });
          }
      }).finally(()=>setLoading(false));
  
    },[document, id]);


    const getAsync = async (response: AxiosResponse<any, any>) => {
      try {
        const res = await apiInstance.get(
          `${REGISTER_PAYMENT_SUPPORT}-status/${response.data.id}`
        );
        if (res.status === 202) {
          setTimeout(() => getAsync(res), 7000);
        } else if((res.data === 'FAILED')) {
          Swal.fire({
              title: "Error cargando documentos.",
              text: res.data,
              icon: 'error',
              timer: 8500,
            });
          setIsLoading(false);
        }else{
          res.status === 206
          ? downloadDocument(res, "Los documentos tienen problemas de validacíon", downloadsDescription.errorRips, 'error')
          : Swal.fire({
              title: 'Carga exitosa',
              text: 'Los archivos se han procesado correctamente.',
              icon: 'success',
              timer: 8500,
            }).then(() => {
              cleanDocuments();
              navigate(-1);
            });
          setIsLoading(false);
          }
      } catch (error) {
        
      }
    }

    const loadAsyncFiles = async () => {
      setIsLoading(true)
      try {
        const formData = new FormData();
        const paymentsBlob = new Blob(
            [JSON.stringify(nameSupports)],
            { type: 'application/json' }
          );
        formData.append('paymentName', paymentsBlob);
        supports.forEach((doc, idx) => {
          if (doc.file) {
            formData.append('files', doc.file);
          }
        });

        const response = await apiInstance.post(
          `${REGISTER_PAYMENT_SUPPORT}/${id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        getAsync(response);
        
        } catch (error) {
          setIsLoading(false);
          console.error(error)
        }
    }

    const handleChangeFileDocument = ( file: File, doc:any ) => {
        if(doc.name !== file.name){
          errorNameFile();
          return;
        }

        if (file && file.size < (7000000) ) {
          const currentDocs =  supports.map(s => s.code === doc.code ? {...s, file, originalName: file.name} : s);
          setHasErrors(false);
          dispatch.linkSupports.addSupports(currentDocs);
        }
      };


    const errorTypeFile = () => {
        Swal.fire({
          title: 'Carga fallida',
          text: `El Archivo cargado no es válido, tipo de archivo incorrecto`,
          icon: 'error',
          timer: 5500
        })
      }
      
    const errorSizeFile = () => {
        Swal.fire({
            title: 'Carga fallida',
            text: `El Archivo cargado no es válido, el archivo debe ser menor a ${fileLengthDocument}MB`,
            icon: 'error',
            timer: 5500
        })
    }
    const errorNameFile = () => {
        Swal.fire({
            title: 'Carga fallida',
            text: `El Archivo no tiene un nombre valido`,
            icon: 'error',
            timer: 5500
        })
    }

    const removeFileDocument = (file: ISupports) => {
        const currentDocs =  supports.map(doc => doc.code === file.code ? {code: doc.code, name:doc.name, id: doc.id} : doc);
        const filterDocuments = response?.filter(doc => doc.originalName !== file.originalName);
        setHasErrors(false);
        dispatch.linkSupports.addSupports(currentDocs);
        dispatch.linkSupports.getLink(filterDocuments);
    };

  return {
    typeAcceptedDocument,
    fileLengthDocument,
    supports,
    hasErrors,
    typeAccept,
    fileLength,
    originalName,
    document,
    isDisabled,
    loading,
    isLoading,
    handleChangeFileXlsx,
    removeFileXlsx,
    errorSizeFile,
    errorTypeFile,
    handleChangeFileDocument,
    removeFileDocument,
    loadAsyncFiles,
    uploadXlsx,
    cleanDocuments
  }
}
